import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

export default function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { completeSignIn } = useAuth();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) return;
    (async () => {
      try {
        const u = await completeSignIn(token);
        toast.success("Welcome back", {
          description: `Signed in as ${u.email}. Your saved routines and checks are ready.`,
        });
        navigate("/check", { replace: true });
      } catch (err) {
        toast.error("That sign-in link didn't work", {
          description: err?.response?.data?.detail || "It may have already been used, or expired. Request a fresh one from the sign-in screen.",
        });
      }
    })();
  }, [params, completeSignIn, navigate]);

  const error = !params.get("token");

  return (
    <main
      data-testid="verify-page"
      className="min-h-[calc(100vh-64px)] flex items-center justify-center px-6 bg-cream"
    >
      <div className="max-w-md text-center">
        {error ? (
          <>
            <span className="inline-flex w-14 h-14 rounded-2xl bg-avoid-bg text-avoid-deep items-center justify-center border border-avoid-border">
              <AlertTriangle className="w-6 h-6" />
            </span>
            <h1 className="font-bold text-2xl text-ink mt-5 tracking-[-0.02em]">
              Missing token in link.
            </h1>
            <button
              data-testid="verify-back-home"
              onClick={() => navigate("/")}
              className="mt-6 text-sm text-ink-soft underline underline-offset-4"
            >
              Back to home
            </button>
          </>
        ) : (
          <>
            <span className="inline-flex w-14 h-14 rounded-2xl bg-safe-bg text-safe-deep items-center justify-center border border-safe-border">
              <ShieldCheck className="w-6 h-6" />
            </span>
            <h1 className="font-bold text-2xl text-ink mt-5 tracking-[-0.02em]">
              Signing you in…
            </h1>
            <div className="mt-6 space-y-2">
              <div className="skeleton h-3 w-2/3 mx-auto" />
              <div className="skeleton h-3 w-1/2 mx-auto" />
            </div>
          </>
        )}
      </div>
    </main>
  );
}
